import React, { useMemo } from "react";
import styled from "@emotion/styled";
import { Doughnut } from "react-chartjs-2";
import { connect } from "react-redux";
import Paper from "@mui/material/Paper";

import Placeholder from "components/Placeholder";
import CommonUtils from "services/common/utils";
import ParkkiTheme from "themes/ParkkiTheme";
import { FormattedMessage, useIntl } from "react-intl";

const Container = styled(Paper)`
  padding: ${ParkkiTheme.spacings.base};
  display: flex;
  flex-direction: column;
  min-height: 100px;
  justify-content: center;
  align-items: center;
`;

const GraphContainer = styled.div`
  width: 80%;
`;
const LegendContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: ${ParkkiTheme.spacings.base};
  color: ${ParkkiTheme.colors.darkGrey};
`;

const ValueLabel = styled.span`
  font-weight: 600;
  font-size: ${ParkkiTheme.fontSizes.small};
  color: ${(props) => props?.color};
`;

function PathDistributionLayout({
  initialized,
  stats,
  inOutDistributionByPercentage,
}) {
  const { formatMessage } = useIntl();
  const data = useMemo(() => {
    if (!initialized) return null;
    CommonUtils.resetColorFactoryIndex();

    return stats?.distribution?.reduce((result, currentStat) => {
      const currentTotal = currentStat?.values.reduce((sum, obj) => {
        return inOutDistributionByPercentage
          ? Math.trunc((sum + obj?.percentage) * 10) / 10
          : sum + obj?.value;
      }, 0);

      return {
        ...result,
        total: (result?.total || 0) + currentTotal,
        labels: [...(result?.labels || []), currentStat?.name],
        datasets: [
          {
            ...(result?.datasets?.[0] || {}),
            data: [...(result?.datasets?.[0]?.data || []), currentTotal],
            backgroundColor: [
              ...(result?.datasets?.[0]?.backgroundColor || []),
              CommonUtils.getNewColor(),
            ],
          },
        ],
      };
    }, {});
  }, [inOutDistributionByPercentage, initialized, stats?.distribution]);

  const options = useMemo(() => {
    return {
      rotation: 1 * Math.PI,
      circumference: 1 * Math.PI,
      legend: {
        display: false,
      },
      tooltips: {
        callbacks: {
          label: (tooltipItem, data) => {
            const dataset = data?.datasets?.[0];
            const label = data?.labels?.[tooltipItem?.index];

            const value = dataset?.data?.[tooltipItem?.index];
            return ` ${label} : ${value || 0} ${
              inOutDistributionByPercentage
                ? "%"
                : formatMessage(
                    { id: "flowScene.pathDistribution.traffic" },
                    { count: value ?? 2 }
                  )
            }`;
          },
        },
      },
    };
  }, [formatMessage, inOutDistributionByPercentage]);

  if (!initialized) {
    return <Placeholder height="320px" delay={0} />;
  }

  return (
    <Container>
      <GraphContainer>
        <Doughnut data={data} options={options} onElementsClick={() => {}} />
      </GraphContainer>
      <LegendContainer>
        {data?.labels?.map((label, index) => {
          return (
            <div key={label}>
              <ValueLabel color={data?.datasets?.[0]?.backgroundColor?.[index]}>
                {`${label} : `}
                {inOutDistributionByPercentage ? (
                  `${data?.datasets?.[0]?.data?.[index]} %`
                ) : (
                  <>
                    {`${data?.datasets?.[0]?.data?.[index]} `}
                    <FormattedMessage
                      id="flowScene.pathDistribution.traffic"
                      values={{
                        count: data?.datasets?.[0]?.data?.[index] ?? 1,
                      }}
                    />
                  </>
                )}
              </ValueLabel>
            </div>
          );
        })}
      </LegendContainer>
    </Container>
  );
}

const mapStateToProps = (state) => ({
  inOutDistributionByPercentage:
    state?.Flow.filters.inOutDistributionByPercentage,
});

export default connect(mapStateToProps)(PathDistributionLayout);
